import type { Lang } from './languages.js';
import { LANGS, sortLangs, uniqueLangs } from './languages.js';
import type { LexiconSet, Tier } from './lexicon.js';
import { requireLexicon } from './lexicon.js';
import type { LanguageRequirement } from './modes.js';
import { RNG_VERSION, Rng } from './rng.js';
import type { RuleSet } from './rules.js';
import { wordPoints } from './rules.js';
import { SPECIAL_TILE_LANGS, TILE_ALPHABET, TILE_COUNT, VOWEL_TILES } from './tiles.js';

export const DRAW_GENERATOR_VERSION = `draw-v1+${RNG_VERSION}`;

/** A player's configuration that the common draw must suit. */
export interface DrawConfig {
  languages: Lang[];
  requirement: LanguageRequirement;
  targetWords: number | null;
}

export interface LanguageAnalysis {
  lang: Lang;
  total: number;
  common: number;
  /** Common words by length (index = tiles). */
  commonByLength: number[];
  longestCommon: number;
  /** Sum of the base points of every common word (score potential). */
  commonPoints: number;
}

export interface ConfigAnalysis {
  languages: Lang[];
  perLanguage: LanguageAnalysis[];
  /** Distinct common keys over the configuration's languages (a homograph counts once). */
  commonDistinct: number;
  /** Quota of the contract reachable with common words, homographs attributed once. */
  quotaFeasible: boolean;
  ok: boolean;
  failures: string[];
}

/**
 * Maximum bipartite assignment check: can each language receive `quota` distinct keys, every
 * key being attributed to at most one language? (Homographs never fill two counters.)
 */
export function quotaAssignmentFeasible(
  keysByLang: ReadonlyMap<Lang, readonly string[]>,
  quota: number,
): boolean {
  if (quota <= 0) return true;
  const langs = [...keysByLang.keys()];
  // Slots: each language repeated `quota` times; match slots to keys (Kuhn's algorithm).
  const slots: Lang[] = [];
  for (const l of langs) for (let i = 0; i < quota; i++) slots.push(l);
  const owner = new Map<string, number>(); // key → slot index
  const tryAssign = (slot: number, seen: Set<string>): boolean => {
    for (const key of keysByLang.get(slots[slot]!) ?? []) {
      if (seen.has(key)) continue;
      seen.add(key);
      const current = owner.get(key);
      if (current === undefined || tryAssign(current, seen)) {
        owner.set(key, slot);
        return true;
      }
    }
    return false;
  };
  for (let s = 0; s < slots.length; s++) {
    if (!tryAssign(s, new Set())) return false;
  }
  return true;
}

export function analyzeConfig(
  rack: readonly string[],
  config: DrawConfig,
  lexicons: LexiconSet,
  rules: RuleSet,
): ConfigAnalysis {
  const commonMax: Tier = rules.draw.commonMaxTier;
  const perLanguage: LanguageAnalysis[] = [];
  const keysByLang = new Map<Lang, string[]>();
  const union = new Set<string>();
  for (const lang of config.languages) {
    const lex = requireLexicon(lexicons, lang);
    const idx = lex.formableIndices(rack);
    const commonByLength = new Array<number>(rules.maxWordLength + 1).fill(0);
    const commonKeys: string[] = [];
    let longest = 0;
    let points = 0;
    for (const i of idx) {
      if (lex.tierAt(i) > commonMax) continue;
      const key = lex.keyAt(i);
      commonKeys.push(key);
      union.add(key);
      commonByLength[key.length] = (commonByLength[key.length] ?? 0) + 1;
      if (key.length > longest) longest = key.length;
      points += wordPoints(key.length, rules);
    }
    keysByLang.set(lang, commonKeys);
    perLanguage.push({
      lang,
      total: idx.length,
      common: commonKeys.length,
      commonByLength,
      longestCommon: longest,
      commonPoints: points,
    });
  }

  const failures: string[] = [];
  const multi = config.languages.length > 1;
  const minPerLang = multi
    ? rules.draw.minCommonPerLanguageMulti
    : rules.draw.minCommonSingleLanguage;
  for (const a of perLanguage) {
    if (a.common < minPerLang) failures.push(`min_common:${a.lang}`);
  }
  if (union.size < rules.draw.minCommonTotal) failures.push('min_common_total');
  if (Math.max(0, ...perLanguage.map((a) => a.longestCommon)) < rules.draw.minLongestCommonLength) {
    failures.push('no_long_word');
  }
  const quota = config.requirement.quotaPerLanguage;
  const quotaFeasible = quotaAssignmentFeasible(keysByLang, quota);
  if (!quotaFeasible) failures.push('quota_infeasible');
  if (
    config.targetWords !== null &&
    union.size < config.targetWords * rules.draw.targetSlackFactor
  ) {
    failures.push('target_infeasible');
  }
  return {
    languages: [...config.languages],
    perLanguage,
    commonDistinct: union.size,
    quotaFeasible,
    ok: failures.length === 0,
    failures,
  };
}

/** Tile weights for a set of languages: average of each language's common-word letter frequency. */
export function tileWeights(langs: readonly Lang[], lexicons: LexiconSet): Float64Array {
  const w = new Float64Array(TILE_COUNT);
  const sorted = sortLangs(uniqueLangs(langs));
  for (const lang of sorted) {
    const f = requireLexicon(lexicons, lang).letterFrequencies(2);
    for (let t = 0; t < TILE_COUNT; t++) w[t] = w[t]! + f[t]! / sorted.length;
  }
  // Special tiles only when a language using them is in play.
  for (let t = 0; t < TILE_COUNT; t++) {
    const users = SPECIAL_TILE_LANGS[TILE_ALPHABET[t]!];
    if (users && !users.some((l) => sorted.includes(l))) w[t] = 0;
  }
  return w;
}

/** Samples a rack respecting the structural constraints (vowels, repeats, special tiles, Q/U). */
export function sampleRack(rng: Rng, weights: Float64Array, rules: RuleSet): string[] {
  const d = rules.draw;
  const vowelIdx: number[] = [];
  const consIdx: number[] = [];
  for (let t = 0; t < TILE_COUNT; t++) {
    if ((weights[t] ?? 0) <= 0) continue;
    (VOWEL_TILES.has(TILE_ALPHABET[t]!) ? vowelIdx : consIdx).push(t);
  }
  const vowelCount = d.vowelsMin + rng.int(d.vowelsMax - d.vowelsMin + 1);
  const counts = new Uint8Array(TILE_COUNT);
  let specials = 0;
  const allIdx = [...vowelIdx, ...consIdx];
  const drawFrom = (preferred: number[], maxSame: number): number => {
    const isSpecialCapped = (t: number): boolean =>
      SPECIAL_TILE_LANGS[TILE_ALPHABET[t]!] !== undefined && specials >= d.maxSpecialTiles;
    // Relax constraints progressively when the lexicons use very few letters (never for real languages).
    let pool = preferred;
    let ws = pool.map((t) => ((counts[t] ?? 0) >= maxSame || isSpecialCapped(t) ? 0 : weights[t]!));
    if (!ws.some((w) => w > 0)) {
      pool = allIdx;
      ws = pool.map((t) => (isSpecialCapped(t) ? 0 : weights[t]!));
    }
    const t = pool[rng.weightedIndex(ws)]!;
    counts[t] = (counts[t] ?? 0) + 1;
    if (SPECIAL_TILE_LANGS[TILE_ALPHABET[t]!]) specials++;
    return t;
  };
  const tiles: number[] = [];
  for (let i = 0; i < vowelCount; i++) tiles.push(drawFrom(vowelIdx, d.maxSameVowel));
  for (let i = vowelCount; i < rules.rackSize; i++)
    tiles.push(drawFrom(consIdx, d.maxSameConsonant));
  const letters = tiles.map((t) => TILE_ALPHABET[t]!);
  // A Q is only useful with a U.
  const qi = letters.indexOf('Q');
  if (qi >= 0 && !letters.includes('U')) {
    const vi = letters.findIndex((l) => VOWEL_TILES.has(l) && l !== 'U');
    if (vi >= 0) letters[vi] = 'U';
    else letters[qi] = 'S';
  }
  return rng.shuffle(letters);
}

export interface DrawRequest {
  seed: string;
  configs: DrawConfig[];
  lexicons: LexiconSet;
  rules: RuleSet;
  /** Pre-validated racks used when the bounded generation budget is exhausted. */
  pool?: readonly (readonly string[])[];
}

export type DrawResult =
  | {
      ok: true;
      rack: string[];
      seed: string;
      generatorVersion: string;
      source: 'generated' | 'pool';
      attempts: number;
      analyses: ConfigAnalysis[];
    }
  | {
      ok: false;
      reason: 'no_draw_available';
      seed: string;
      generatorVersion: string;
      attempts: number;
      /** How often each check failed, to propose an explicit alternative to the players. */
      failureStats: Record<string, number>;
    };

export function checkRack(
  rack: readonly string[],
  configs: readonly DrawConfig[],
  lexicons: LexiconSet,
  rules: RuleSet,
): ConfigAnalysis[] {
  return configs.map((c) => analyzeConfig(rack, c, lexicons, rules));
}

/**
 * Generates a common draw suiting every configuration, with a bounded budget.
 * Same inputs (seed, configurations, rules, lexicon versions) ⇒ same rack.
 */
export function generateDraw(req: DrawRequest): DrawResult {
  const { rules, lexicons, configs } = req;
  const rng = new Rng(`${DRAW_GENERATOR_VERSION}|${req.seed}`);
  const langs = uniqueLangs(configs.flatMap((c) => c.languages));
  const weights = tileWeights(langs, lexicons);
  const failureStats: Record<string, number> = {};
  let attempts = 0;
  for (; attempts < rules.draw.maxAttempts;) {
    attempts++;
    const rack = sampleRack(rng, weights, rules);
    const analyses = checkRack(rack, configs, lexicons, rules);
    if (analyses.every((a) => a.ok)) {
      return {
        ok: true,
        rack,
        seed: req.seed,
        generatorVersion: DRAW_GENERATOR_VERSION,
        source: 'generated',
        attempts,
        analyses,
      };
    }
    for (const a of analyses)
      for (const f of a.failures) failureStats[f] = (failureStats[f] ?? 0) + 1;
  }
  if (req.pool && req.pool.length > 0) {
    const start = rng.int(req.pool.length);
    for (let i = 0; i < req.pool.length; i++) {
      const rack = [...req.pool[(start + i) % req.pool.length]!];
      const analyses = checkRack(rack, configs, lexicons, rules);
      if (analyses.every((a) => a.ok)) {
        return {
          ok: true,
          rack: rng.shuffle(rack),
          seed: req.seed,
          generatorVersion: DRAW_GENERATOR_VERSION,
          source: 'pool',
          attempts,
          analyses,
        };
      }
    }
  }
  return {
    ok: false,
    reason: 'no_draw_available',
    seed: req.seed,
    generatorVersion: DRAW_GENERATOR_VERSION,
    attempts,
    failureStats,
  };
}

/** Every language subset of size 1–3 (41 subsets for 6 languages), in canonical order. */
export function languageSubsets(maxSize = 3, langs: readonly Lang[] = LANGS): Lang[][] {
  const out: Lang[][] = [];
  const rec = (start: number, acc: Lang[]): void => {
    if (acc.length > 0) out.push([...acc]);
    if (acc.length === maxSize) return;
    for (let i = start; i < langs.length; i++) rec(i + 1, [...acc, langs[i]!]);
  };
  rec(0, []);
  return out;
}
